import { useState, useCallback, useRef } from 'react'
import { Loader2, Upload, CheckCircle, Phone, Target, XCircle, Briefcase, DollarSign, CreditCard, Clock, TrendingUp, Wallet, MousePointer, Eye, BarChart3 } from 'lucide-react'
import type { AdWordsInitialData, AdWordsAnalytics, AdWordsSummary, NotBookedData } from './types'
import { fetchStats, uploadGoogleAdsFile } from './api'
import { cn } from '@/lib/utils'

// Reuse eLocal components
import { ConversionFunnel } from '../elocal-analytics/components/ConversionFunnel'
import { PaymentFunnel } from '../elocal-analytics/components/PaymentFunnel'
import { NotBookedBreakdown } from '../elocal-analytics/components/NotBookedBreakdown'
import { AdWordsLeadsTable } from './components/AdWordsLeadsTable'

interface Props {
  initialData: AdWordsInitialData
}

function fmt(val: number): string {
  return `$${val.toLocaleString('en-US')}`
}

interface CardProps {
  title: string
  icon: React.ElementType
  value: string | number
  subtitle: string
  iconColor?: string
  valueColor?: string
}

function Card({ title, icon: Icon, value, subtitle, iconColor = 'text-gray-400', valueColor = 'text-gray-900' }: CardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <p className={cn('text-3xl font-bold', valueColor)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  )
}

function SummaryCards({ data }: { data: AdWordsSummary }) {
  return (
    <div className="space-y-4">
      {/* Row 1: Lead Funnel */}
      <div className="grid grid-cols-6 gap-4">
        <Card title="Total Leads" icon={BarChart3} value={data.total_leads} subtitle={`${fmt(data.cost_per_lead)} per lead`} iconColor="text-blue-500" />
        <Card title="Calls" icon={Phone} value={data.calls} subtitle="CallRail calls" iconColor="text-indigo-500" valueColor="text-indigo-600" />
        <Card title="Forms & Other" icon={MousePointer} value={data.forms} subtitle="ADS source, no CallRail" iconColor="text-cyan-500" valueColor="text-cyan-600" />
        <Card title="Booked" icon={Target} value={data.booked} subtitle="ADS Workiz jobs" iconColor="text-purple-500" valueColor="text-purple-600" />
        <Card title="Cancelled" icon={XCircle} value={data.cancelled} subtitle={`${data.booked > 0 ? ((data.cancelled / data.booked) * 100).toFixed(0) : 0}% cancel rate`} iconColor="text-red-500" valueColor="text-red-500" />
        <Card title="Jobs" icon={Briefcase} value={data.jobs} subtitle={`${fmt(data.cost_per_job)} per job`} iconColor="text-green-500" valueColor="text-green-600" />
      </div>

      {/* Row 2: Financial */}
      <div className="grid grid-cols-5 gap-4">
        <Card title="Spent" icon={DollarSign} value={fmt(data.spent)} subtitle={`${data.total_clicks.toLocaleString()} clicks`} iconColor="text-red-500" valueColor="text-red-600" />
        <Card title="Collected" icon={CreditCard} value={fmt(data.collected)} subtitle="Paid payments" iconColor="text-green-500" valueColor="text-green-600" />
        <Card title="Outstanding" icon={Clock} value={fmt(data.outstanding)} subtitle="Revenue - Collected" iconColor="text-amber-500" valueColor="text-amber-600" />
        <Card title="Revenue" icon={TrendingUp} value={fmt(data.revenue)} subtitle="Job totals (non-cancelled)" iconColor="text-blue-500" />
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Company Net</h3>
            <Wallet className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600">{fmt(data.company_net)}</p>
          <div className="text-xs text-gray-400 mt-1 space-y-0.5">
            <p>{fmt(data.revenue)} - 10% = {fmt(data.after_parts)}</p>
            <p>{fmt(data.after_parts)} / 2 = <span className="font-semibold text-emerald-600">{fmt(data.company_net)}</span></p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AdWordsAnalyticsDashboard({ initialData }: Props) {
  const [periodMonth, setPeriodMonth] = useState(initialData.period_month)
  const [analytics, setAnalytics] = useState<AdWordsAnalytics | null>(initialData.analytics)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [spendUploaded, setSpendUploaded] = useState(initialData.spend_uploaded)
  const fileRef = useRef<HTMLInputElement>(null)

  const refreshAnalytics = useCallback(async (month: string) => {
    setLoading(true)
    try {
      const data = await fetchStats(month)
      setAnalytics(data)
    } catch { /* keep existing */ } finally {
      setLoading(false)
    }
  }, [])

  const handleMonthChange = useCallback((month: string) => {
    setPeriodMonth(month)
    window.location.href = `/dashboard/adwords-analytics?period_month=${month}`
  }, [])

  const handleUpload = useCallback(async (file: File) => {
    setUploading(true)
    try {
      await uploadGoogleAdsFile(periodMonth, file)
      setSpendUploaded(true)
      refreshAnalytics(periodMonth)
    } catch { /* ignore */ } finally {
      setUploading(false)
    }
  }, [periodMonth, refreshAnalytics])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AdWords Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Google Ads + CallRail lead effectiveness</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Period:</label>
          {initialData.available_months.length > 0 && (
            <select value={periodMonth} onChange={(e) => handleMonthChange(e.target.value)}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg bg-white">
              {initialData.available_months.map((m) => (
                <option key={m} value={m}>
                  {new Date(m + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
          )}
          <input type="month" value={periodMonth} onChange={(e) => handleMonthChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg" />
        </div>
      </div>

      {/* Google Ads Spend Upload */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {spendUploaded ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <DollarSign className="w-6 h-6 text-gray-400" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-700">Google Ads Spend</p>
              <p className="text-xs text-gray-500">{spendUploaded ? 'Uploaded' : 'Upload Google Ads cost CSV'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = '' }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {spendUploaded ? 'Re-upload' : 'Upload CSV'}
            </button>
            <span className="text-xs text-gray-400">CallRail data loads automatically</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-500">Loading analytics...</span>
        </div>
      )}

      {!loading && analytics && (
        <>
          <SummaryCards data={analytics.summary} />
          <NotBookedBreakdown data={analytics.not_booked} />
          <div className="grid grid-cols-2 gap-6">
            <ConversionFunnel funnel={analytics.funnel} />
            <PaymentFunnel data={analytics.payment_funnel} />
          </div>
          <AdWordsLeadsTable
            initialLeads={analytics.leads_detail.slice(0, 25)}
            periodMonth={periodMonth}
            totalLeads={analytics.leads_detail.length}
          />
        </>
      )}

      {!loading && !analytics && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
          <p className="text-gray-500 text-lg">No data available</p>
          <p className="text-gray-400 text-sm mt-2">Upload Workiz Jobs CSV on eLocal page first, then upload Google Ads spend here</p>
        </div>
      )}
    </div>
  )
}
